import { useState, useEffect, useMemo } from 'react';

// packages
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { FormattedMessage, injectIntl } from 'react-intl';

// components
import Button from '../../../../components/Button/Button';
import RadioButton from '../../../../components/RadioButton/RadioButton';
import ModernCurrencyInput from '../../../../components/ModernCurrencyInput/ModernCurrencyInput';
import ToolTip from '../../../../components/Tooltip/Tooltip';

// styles
import Styles from './marketForm.module.css';
import ShimmerLine from '../../../../components/shimmer-components/ShimmerLine';

// icons
import { ReactComponent as FlagIcon } from '../../../../assets/icons/imported/finish.svg';
import { ReactComponent as MarketIcon } from '../../../../assets/icons/imported/market.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/imported/info.svg';

// redux
import { _addOrder } from '../../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { _getBalances } from '../../../../redux/actions';
import { setBalances } from '../../../../redux/slices/balancesSlice';
import { setSnackbarData, setSnackbarOpen } from '../../../snackbar/redux/snackbarSlice';
import { startPolling } from '../../../../redux/slices/orderPollingSlice';

// other
import { formatNumber } from '../../../helpers/formatNumber';
import { estimateUnitValue } from '../../../helpers/estimateValueBase';
import { API } from '../../../../utility/API';

const sliderMarks = {
  0: '0%',
  25: '25%',
  50: '50%',
  75: '75%',
  100: '100%'
}

function MarketForm() {
  const dispatch = useDispatch()
  //user data
  const userData = useSelector(state => state.userData?.value)
  const balances = useSelector(state => state.balances.value)
  const loggedIn = useSelector(state => state.loggedIn.value)

  const marketsList = useSelector(state => state.marketList.value)
  const selectedMarket = useSelector(state => state.selectedMarket.value)
  const reduxLimitPrice = useSelector(state => state.limitPrice.value)
  const [buyQuantity, setBuyQuantity] = useState('')
  const [sellQuantity, setSellQuantity] = useState('')
  const [sellPrice, setSellPrice] = useState('')
  const [buyPrice, setBuyPrice] = useState('')
  const [toCurrency, setToCurrency] = useState(marketsList.find(el => el.id === selectedMarket?.id)?.quote_unit)
  const [fromCurrency, setFromCurrency] = useState(marketsList.find(el => el.id === selectedMarket?.id)?.base_unit)
  const [selectedRadioButton, setSelectedRadioButton] = useState('limit')
  const marketTickers = useSelector(state => state.marketTickers.value)
  const orderBookAsks = useSelector(state => state.orderBook.asks)
  const currencies = useSelector(state => state.currencies.value)
  const [buyLimitPrice, setBuyLimitPrice] = useState('')
  const [sellLimitPrice, setSellLimitPrice] = useState('')
  const [refreshBalance, setRefreshBalance] = useState(false)
  const [buySliderValue, setBuySliderValue] = useState(0)
  const [sellSliderValue, setSellSliderValue] = useState(0)
  const [buyTotalPrice, setBuyTotalPrice] = useState('')
  const [sellTotalPrice, setSellTotalPrice] = useState('')
  const [sellConvertedPrice, setSellConvertedPrice] = useState('')
  const [buyConvertedPrice, setBuyConvertedPrice] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [formError, setFormError] = useState(false)
  const [buyFee, setBuyFee] = useState(null)
  const [sellFee, setSellFee] = useState(null)
  const [buyFeeLoading, setBuyFeeLoading] = useState(false)
  const [sellFeeLoading, setSellFeeLoading] = useState(false)
  const [buyFeeCalculationTimer, setBuyFeeCalculationTimer] = useState(null)
  const [sellFeeCalculationTimer, setSellFeeCalculationTimer] = useState(null)
  const [isMaxBuyActive, setIsMaxBuyActive] = useState(false)
  const [isMaxSellActive, setIsMaxSellActive] = useState(false)

  // allowed_to_trade as reported by estimate_fee. It is an object keyed by
  // currency (e.g. { usd: '...', btc: '...' }) but older responses returned a
  // bare number, so both shapes are handled. Buy side is capped in the quote
  // currency, sell side in the base currency.
  const readAllowedToTrade = (fee, currency, fallbackKey) => {
    if (!fee || fee.allowed_to_trade === undefined || fee.allowed_to_trade === null) return null

    const allowed = fee.allowed_to_trade
    if (typeof allowed === 'object') {
      const raw = allowed[currency] ?? (fallbackKey ? allowed[fallbackKey] : undefined)
      if (raw === undefined || raw === null) return null
      const parsed = parseFloat(raw)
      return Number.isFinite(parsed) ? parsed : null
    }

    const parsed = parseFloat(allowed)
    return Number.isFinite(parsed) ? parsed : null
  }

  const getBuyAllowedToTrade = () => readAllowedToTrade(buyFee, toCurrency, 'usd')
  const getSellAllowedToTrade = () => readAllowedToTrade(sellFee, fromCurrency, selectedMarket?.base_unit)

  // Balance validation for buy orders
  const isBuyAmountExceedingBalance = useMemo(() => {
    if (!loggedIn || !balances || !toCurrency) return false

    const balance = balances.find(el => el.currency === toCurrency)?.balance || 0
    const buyAmount = selectedRadioButton === 'limit'
      ? parseFloat(buyQuantity || 0) * parseFloat(buyLimitPrice || 0)
      : parseFloat(buyPrice || 0)

    return parseFloat(balance) < buyAmount
  }, [balances, toCurrency, buyQuantity, buyLimitPrice, buyPrice, selectedRadioButton, loggedIn])

  // Balance validation for sell orders
  const isSellAmountExceedingBalance = useMemo(() => {
    if (!loggedIn || !balances || !fromCurrency) return false

    const balance = balances.find(el => el.currency === fromCurrency)?.balance || 0
    const sellAmount = parseFloat(sellQuantity || 0)

    return parseFloat(balance) < sellAmount
  }, [balances, fromCurrency, sellQuantity, loggedIn])

  // Function to check if buy amount exceeds allowed_to_trade limit (in quote currency / USD)
  const isBuyAmountExceedsAllowedLimit = useMemo(() => {
    // allowed_to_trade.usd already accounts for the fee, so it is the max spend
    // as reported by the backend.
    const maxSpendable = getBuyAllowedToTrade()
    if (maxSpendable === null) return false

    const currentUsdAmount = selectedRadioButton === 'limit'
      ? parseFloat(buyQuantity || 0) * parseFloat(buyLimitPrice || 0)
      : parseFloat(buyPrice || 0)

    const tolerance = isMaxBuyActive ? 0.01 : 0

    return currentUsdAmount > (maxSpendable + tolerance)
  }, [buyFee, buyQuantity, buyPrice, buyLimitPrice, selectedRadioButton, toCurrency, isMaxBuyActive])

  // Function to check if sell amount exceeds allowed_to_trade limit
  const isSellAmountExceedsAllowedLimit = useMemo(() => {
    // Compare the base currency amount (quantity) with allowed_to_trade
    const allowedQuantity = getSellAllowedToTrade()
    if (allowedQuantity === null) return false

    const currentQuantity = parseFloat(sellQuantity || 0)

    // Add small tolerance for precision differences when MAX is active
    const tolerance = isMaxSellActive ? 0.00001 : 0 // 0.00001 BTC tolerance (~$1)

    return currentQuantity > (allowedQuantity + tolerance)
  }, [sellFee, sellQuantity, fromCurrency, selectedMarket, isMaxSellActive])

  // Combined validation for buy button disabling
  const isBuyButtonDisabled = useMemo(() => {
    const hasMinAmountError = submitted &&
      parseFloat(buyQuantity || buyConvertedPrice || 0) <
      parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount || 0)

    const hasNoAmount = !buyQuantity && !buyConvertedPrice && !buyPrice

    return hasMinAmountError || isBuyAmountExceedingBalance || isBuyAmountExceedsAllowedLimit || hasNoAmount
  }, [submitted, buyQuantity, buyConvertedPrice, buyPrice, marketsList, selectedMarket, isBuyAmountExceedingBalance, isBuyAmountExceedsAllowedLimit])

  // Combined validation for sell button disabling
  const isSellButtonDisabled = useMemo(() => {
    const hasMinAmountError = submitted &&
      parseFloat(sellQuantity || sellConvertedPrice || 0) <
      parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount || 0)

    const hasNoAmount = !sellQuantity && !sellConvertedPrice && !sellPrice

    return hasMinAmountError || isSellAmountExceedingBalance || isSellAmountExceedsAllowedLimit || hasNoAmount
  }, [submitted, sellQuantity, sellConvertedPrice, sellPrice, marketsList, selectedMarket, isSellAmountExceedingBalance, isSellAmountExceedsAllowedLimit])

  // Returns best ask from the live order book, with a 0.3% safety margin for
  // order book lag. Falls back to last_price × 1.015 if no order book data.
  const getBestAskWithBuffer = (lastPrice) => {
    if (orderBookAsks && orderBookAsks.length > 0) {
      const bestAsk = parseFloat(orderBookAsks[0][0])
      if (bestAsk > 0) return bestAsk * 1.003
    }
    return lastPrice * 1.015
  }

  // Function to format volume according to market precision
  const formatVolumeToMarketPrecision = (volume, marketId) => {
    if (!volume || !marketId) return volume

    const market = marketsList.find(el => el.id === marketId)
    if (!market) return volume

    const precision = market.step_size || market.amount_precision || 8
    const factor = Math.pow(10, precision)
    return (Math.floor(parseFloat(volume) * factor) / factor).toFixed(precision)
  }

  // Function to validate input against market precision
  const validateDecimalPrecision = (value, marketId) => {
    if (!value || !marketId) return value

    const market = marketsList.find(el => el.id === marketId)
    if (!market) return value

    // Get precision from market data - use amount_precision for base currency amounts
    const precision = market.amount_precision || market.step_size || 8

    // Split the value by decimal point
    const parts = value.toString().split('.')

    // If there's a decimal part and it exceeds the precision
    if (parts.length > 1 && parts[1].length > precision) {
      // Truncate to the allowed precision
      return `${parts[0]}.${parts[1].substring(0, precision)}`
    }

    return value
  }

  const handleBuySliderChange = value => {
    setBuySliderValue(value)
    let newQuantity = ''
    let newPrice = ''

    if (selectedRadioButton === 'limit') {
      // For limit orders, use base currency (fromCurrency) for quantity calculation
      const currency = balances.find(el => el.currency === fromCurrency)
      if (buyLimitPrice && currency) {
        newQuantity = (currency.balance * value) / 100
        setBuyQuantity(newQuantity)
        // Calculate fee with the new quantity
        if (newQuantity > 0 && buyLimitPrice > 0) {
          debouncedCalculateBuyFee(newQuantity.toString(), null, buyLimitPrice.toString())
        }
      }
    } else {
      // For market orders, use fee-adjusted balance (same logic as MAX button)
      // Calculate: Available Balance - Fees Applied to Available Balance (FIXED amount)
      const rawBalance = parseFloat(balances.find(el => el.currency === toCurrency)?.balance || 0)
      const feePercentage = buyFee ? parseFloat(buyFee.fee_percentage || 0) : 0
      const feeAmount = rawBalance * (feePercentage / 100)
      const availableBalance = rawBalance - feeAmount

      newPrice = (availableBalance * value) / 100
      setBuyPrice(newPrice)
      // Calculate fee with the new price
      if (newPrice > 0) {
        debouncedCalculateBuyFee(null, newPrice.toString(), null)
      }
    }
  }

  const handleSellSliderChange = value => {
    setSellSliderValue(value)
    let newQuantity = ''

    // Primary: Use allowed_to_trade from API response (base currency for sell orders)
    let availableAfterFees = 0
    if (sellFee && sellFee.allowed_to_trade) {
      if (typeof sellFee.allowed_to_trade === 'object') {
        // New structure: get base currency amount from allowed_to_trade object
        const baseCurrency = selectedMarket?.base_unit || fromCurrency
        availableAfterFees = parseFloat(sellFee.allowed_to_trade[baseCurrency] || 0)
      } else {
        // Legacy structure: allowed_to_trade is a direct number
        availableAfterFees = parseFloat(sellFee.allowed_to_trade)
      }
    }

    // Fallback: Calculate balance - fees if no API data available
    if (availableAfterFees <= 0) {
      const currency = balances.find(el => el.currency === fromCurrency)
      const rawBalance = parseFloat(currency?.balance || 0)
      const feePercentage = sellFee ? parseFloat(sellFee.fee_percentage || 0) : 0
      const feeAmount = rawBalance * (feePercentage / 100)
      availableAfterFees = rawBalance - feeAmount
    }

    if (selectedRadioButton === 'limit') {
      if (sellLimitPrice) {
        newQuantity = (availableAfterFees * value) / 100
        setSellQuantity(newQuantity)
        // Calculate fee with the new quantity
        if (newQuantity > 0 && sellLimitPrice > 0) {
          debouncedCalculateSellFee(newQuantity.toString(), null, sellLimitPrice.toString())
        }
      }
    } else {
      // For market orders, calculate quantity based on fee-adjusted balance
      newQuantity = (availableAfterFees * value) / 100
      setSellQuantity(newQuantity)

      // Calculate estimated price based on market price
      const marketPrice = parseFloat(marketTickers[selectedMarket?.id]?.last || 0)
      if (marketPrice > 0 && newQuantity > 0) {
        const estimatedPrice = newQuantity * marketPrice
        setSellPrice(estimatedPrice.toString())
      }

      // Calculate fee with the new quantity
      if (newQuantity > 0) {
        debouncedCalculateSellFee(newQuantity.toString(), null, null)
      }
    }
  }
  // ----- RESPONSIVENESS STATE ---------
  // ------------------------------------
  const [modalStates, setModalStates] = useState({
    quickBuySellOpen: false,
    convertOpen: false
  })

  useEffect(() => {
    setSellLimitPrice(parseFloat(reduxLimitPrice.replace(/,/g, '')))
    setBuyLimitPrice(parseFloat(reduxLimitPrice.replace(/,/g, '')))
  }, [reduxLimitPrice])

  const [isTableCollapsed, setIsTableCollapsed] = useState(window.innerWidth < 940)
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 700)

  const handleResize = () => {
    setIsTableCollapsed(window.innerWidth < 900)
    setIsMobileView(window.innerWidth < 700)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  // ------------------------------------
  // ------------------------------------

  useEffect(() => {
    // console.log(currencies)
    setFromCurrency(marketsList.find(el => el.id === selectedMarket.id)?.base_unit)
    setToCurrency(marketsList.find(el => el.id === selectedMarket.id)?.quote_unit)
    setBuyConvertedPrice(
      selectedRadioButton === 'limit'
        ? buyTotalPrice / buyLimitPrice
        : estimateUnitValue(
          buyQuantity ? toCurrency : fromCurrency,
          buyQuantity ? fromCurrency : toCurrency,
          parseFloat(buyQuantity || buyPrice),
          currencies,
          marketsList,
          marketTickers
        )
    )
  }, [
    buyQuantity,
    selectedRadioButton,
    selectedMarket,
    buyPrice,
    currencies,
    marketTickers,
    buyTotalPrice,
    buyConvertedPrice
  ])

  const resetInputValues = () => {
    setSellPrice('')
    setBuyPrice('')
    setBuyQuantity('')
    setSellQuantity('')
    setBuyLimitPrice('')
    setSellLimitPrice('')
    setBuySliderValue(0)
    setSellSliderValue(0)
    setIsMaxBuyActive(false)
    setIsMaxSellActive(false)
  }

  useEffect(() => {
    setBuySliderValue(0)
    setSellSliderValue(0)
    // console.log(userData)
    if (loggedIn) {
      _getBalances(
        res => {
          dispatch(setBalances(res))
        },
        () => { }
      )
    }
  }, [refreshBalance, selectedMarket])

  useEffect(() => {
    resetInputValues()
  }, [selectedMarket, selectedRadioButton])

  useEffect(() => {
    setFromCurrency(marketsList.find(el => el.id === selectedMarket.id)?.base_unit)
    setToCurrency(marketsList.find(el => el.id === selectedMarket.id)?.quote_unit)
    setSellConvertedPrice(
      selectedRadioButton === 'limit'
        ? sellTotalPrice / sellLimitPrice
        : estimateUnitValue(
          sellQuantity ? toCurrency : fromCurrency,
          sellQuantity ? fromCurrency : toCurrency,
          parseFloat(sellQuantity || sellPrice),
          currencies,
          marketsList,
          marketTickers
        )
    )
  }, [sellQuantity, marketTickers, selectedMarket, sellPrice, sellTotalPrice, sellConvertedPrice])

  // Modified input handlers to enforce step_size validation
  const handleBuyQuantityChange = event => {
    let newValue = event.target.value
    console.log('🔢 handleBuyQuantityChange called with:', newValue, 'radioButton:', selectedRadioButton)

    // Reset max state when user manually changes input
    setIsMaxBuyActive(false)

    // Validate against step_size
    if (selectedRadioButton === 'market') {
      newValue = validateDecimalPrecision(newValue, selectedMarket?.id)
    }

    setBuyQuantity(newValue)
    setBuyPrice('')
    setBuyTotalPrice('')

    // For market orders, calculate the estimated price based on the current market rate
    if (selectedRadioButton === 'market' && newValue && parseFloat(newValue) > 0) {
      console.log('💲 Market order - calculating estimated price for quantity:', newValue)
      const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      console.log('🎯 Market price for estimation:', marketPrice)
      console.log('🔍 Market ticker data for estimation:', marketTickers[selectedMarket.id])
      if (marketPrice > 0) {
        const estimatedPrice = parseFloat(newValue) * marketPrice
        setBuyPrice(estimatedPrice.toString())
        console.log('✅ Set estimated price:', estimatedPrice.toString())
      }
    }

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      console.log('🚫 Resetting buy fee - empty or zero value')
      setBuyFee(null)
      return
    }

    // For market orders, calculate fee when quantity is entered (we have market price)
    // For limit orders, calculate fee when we have both quantity and limit price
    if (selectedRadioButton === 'market' && newValue && parseFloat(newValue) > 0) {
      console.log('🚀 Market order - calling debouncedCalculateBuyFee')
      debouncedCalculateBuyFee(newValue, null, null)
    } else if (selectedRadioButton === 'limit' && newValue && parseFloat(newValue) > 0 && buyLimitPrice && parseFloat(buyLimitPrice) > 0) {
      console.log('🚀 Limit order - calling debouncedCalculateBuyFee with limit price')
      debouncedCalculateBuyFee(newValue, null, buyLimitPrice)
    }
  }

  const handleSellQuantityChange = event => {
    let newValue = event.target.value
    console.log('🔢 handleSellQuantityChange called with:', newValue, 'radioButton:', selectedRadioButton)

    // Reset max state when user manually changes input
    setIsMaxSellActive(false)

    // Validate against step_size
    if (selectedRadioButton === 'market') {
      newValue = validateDecimalPrecision(newValue, selectedMarket?.id)
    }

    setSellQuantity(newValue)
    setSellPrice('')
    setSellTotalPrice('')

    // For market orders, calculate the estimated price based on the current market rate
    if (selectedRadioButton === 'market' && newValue && parseFloat(newValue) > 0) {
      console.log('💲 Market order - calculating estimated price for quantity:', newValue)
      const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      if (marketPrice > 0) {
        const estimatedPrice = parseFloat(newValue) * marketPrice
        setSellPrice(estimatedPrice.toString())
        console.log('✅ Set estimated price:', estimatedPrice.toString())
      }
    }

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      console.log('🚫 Resetting sell fee - empty or zero value')
      setSellFee(null)
      return
    }

    // For market orders, calculate fee when quantity is entered (we have market price)
    // For limit orders, calculate fee when we have both quantity and limit price
    if (selectedRadioButton === 'market' && newValue && parseFloat(newValue) > 0) {
      console.log('🚀 Market order - calling debouncedCalculateSellFee')
      debouncedCalculateSellFee(newValue, null, null)
    } else if (selectedRadioButton === 'limit' && newValue && parseFloat(newValue) > 0 && sellLimitPrice && parseFloat(sellLimitPrice) > 0) {
      console.log('🚀 Limit order - calling debouncedCalculateSellFee with limit price')
      debouncedCalculateSellFee(newValue, null, sellLimitPrice)
    }
  }

  const handleBuyPriceChange = event => {
    const newValue = event.target.value
    console.log('💰 handleBuyPriceChange called with:', newValue, 'radioButton:', selectedRadioButton)

    // Reset max state when user manually changes input
    setIsMaxBuyActive(false)

    setBuyPrice(newValue)
    setBuyQuantity('')

    // For market orders, calculate the estimated quantity based on the entered price
    if (selectedRadioButton === 'market' && newValue && parseFloat(newValue) > 0) {
      console.log('📦 Market order - calculating estimated quantity for price:', newValue)
      const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      if (marketPrice > 0) {
        const estimatedQuantity = parseFloat(newValue) / marketPrice
        // Format according to step_size
        const formattedQuantity = formatVolumeToMarketPrecision(estimatedQuantity.toString(), selectedMarket.id)
        setBuyQuantity(formattedQuantity)
        console.log('✅ Set estimated quantity:', formattedQuantity)
      }
    }

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      console.log('🚫 Resetting buy fee - empty or zero value')
      setBuyFee(null)
      return
    }

    // Only calculate fee if there's a valid input - using the NEW value directly
    if (newValue && parseFloat(newValue) > 0) {
      console.log('🚀 Calling debouncedCalculateBuyFee with price:', newValue)
      // Call with immediate value, not waiting for state update
      debouncedCalculateBuyFee(null, newValue, null)
    }
  }

  const handleSellPriceChange = event => {
    const newValue = event.target.value
    console.log('💰 handleSellPriceChange called with:', newValue, 'radioButton:', selectedRadioButton)

    // Reset max state when user manually changes input
    setIsMaxSellActive(false)

    setSellPrice(newValue)
    setSellQuantity('')

    // For market orders, calculate the estimated quantity based on the entered price
    if (selectedRadioButton === 'market' && newValue && parseFloat(newValue) > 0) {
      console.log('📦 Market order - calculating estimated quantity for price:', newValue)
      const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      if (marketPrice > 0) {
        const estimatedQuantity = parseFloat(newValue) / marketPrice
        // Format according to step_size
        const formattedQuantity = formatVolumeToMarketPrecision(estimatedQuantity.toString(), selectedMarket.id)
        setSellQuantity(formattedQuantity)
        console.log('✅ Set estimated quantity:', formattedQuantity)
      }
    }

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      console.log('🚫 Resetting sell fee - empty or zero value')
      setSellFee(null)
      return
    }

    // Only calculate fee if there's a valid input - using the NEW value directly
    if (newValue && parseFloat(newValue) > 0) {
      console.log('🚀 Calling debouncedCalculateSellFee with price:', newValue)
      // Call with immediate value, not waiting for state update
      debouncedCalculateSellFee(null, newValue, null)
    }
  }

  const handleBuyLimitPriceChange = event => {
    const newValue = event.target.value

    // Reset max state when user manually changes input
    setIsMaxBuyActive(false)

    setBuyLimitPrice(newValue)

    // Reset fee if the limit price is deleted and it's needed for calculation
    if ((!newValue || parseFloat(newValue) <= 0) && selectedRadioButton === 'limit') {
      setBuyFee(null)
      return
    }

    // Only calculate fee if there's a valid input and we have a quantity
    if (newValue && parseFloat(newValue) > 0 && buyQuantity && parseFloat(buyQuantity) > 0) {
      // Call with immediate value, not waiting for state update
      debouncedCalculateBuyFee(buyQuantity, null, newValue)
    }
  }

  const handleSellLimitPriceChange = event => {
    const newValue = event.target.value

    // Reset max state when user manually changes input
    setIsMaxSellActive(false)

    setSellLimitPrice(newValue)

    // Reset fee if the limit price is deleted and it's needed for calculation
    if ((!newValue || parseFloat(newValue) <= 0) && selectedRadioButton === 'limit') {
      setSellFee(null)
      return
    }

    // Only calculate fee if there's a valid input and we have a quantity
    if (newValue && parseFloat(newValue) > 0 && sellQuantity && parseFloat(sellQuantity) > 0) {
      // Call with immediate value, not waiting for state update
      debouncedCalculateSellFee(sellQuantity, null, newValue)
    }
  }

  // Max button handlers
  const handleBuyMax = async () => {
    if (!loggedIn || !balances) return

    if (isMaxBuyActive) {
      // Revert: Reset to manual input mode
      setIsMaxBuyActive(false)
      setBuyPrice('')
      setBuyQuantity('')
      setBuyLimitPrice('')
      setBuySliderValue(0)
      setBuyFee(null)
      return
    }

    setIsMaxBuyActive(true)
    setIsMaxSellActive(false)

    const rawBalance = parseFloat(balances.find(el => el.currency === toCurrency)?.balance || 0)

    // Fetch fee first so we get allowed_to_trade before computing the cap
    let feeData = buyFee
    if (!feeData?.allowed_to_trade) {
      try {
        const response = await API.post('trading/market/orders/new/estimate_fee', {
          market_id: selectedMarket.id,
          ord_type: selectedRadioButton,
          price: rawBalance.toString(),
          side: 'buy',
          volume: '1',
          actual_volume: rawBalance.toString()
        })
        feeData = response.data
        setBuyFee(feeData)
      } catch (e) {
        feeData = buyFee
      }
    }

    // allowed_to_trade.usd already accounts for the fee (it equals balance / (1 + fee_rate)),
    // so it is used as-is — no extra safety buffer is applied.
    const allowedUsd = readAllowedToTrade(feeData, toCurrency, 'usd')
    const availableBalance = allowedUsd === null ? rawBalance : Math.min(rawBalance, allowedUsd)

    if (selectedRadioButton === 'limit') {
      setBuyLimitPrice(availableBalance.toString())
      setBuySliderValue(100)
      setBuyQuantity('')
      setBuyPrice('')
    } else {
      setBuyPrice(availableBalance.toString())
      setBuySliderValue(100)

      const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      if (marketPrice > 0) {
        // Use best ask from order book to avoid execution price mismatch
        const executionPrice = getBestAskWithBuffer(marketPrice)
        const estimatedQuantity = availableBalance / executionPrice
        const formattedQuantity = formatVolumeToMarketPrecision(estimatedQuantity.toString(), selectedMarket.id)
        setBuyQuantity(formattedQuantity)
      }

      if (availableBalance > 0) {
        debouncedCalculateBuyFee(null, availableBalance.toString(), null)
      }
    }
  }

  const handleSellMax = () => {
    if (!loggedIn || !balances) return

    if (isMaxSellActive) {
      // Revert: Reset to manual input mode
      setIsMaxSellActive(false)
      setSellPrice('')
      setSellQuantity('')
      setSellLimitPrice('')
      setSellSliderValue(0)
      setSellFee(null)
      return
    }

    setIsMaxSellActive(true)
    setIsMaxBuyActive(false) // Disable buy max if active

    // For sell orders, use the full base currency balance (fees are deducted from received amount),
    // capped by allowed_to_trade so MAX matches the limit shown under the form.
    const balance = parseFloat(balances.find(el => el.currency === fromCurrency)?.balance || 0)
    const allowedQuantity = getSellAllowedToTrade()
    const maxQuantity = allowedQuantity === null ? balance : Math.min(balance, allowedQuantity)
    const formattedQuantity = formatVolumeToMarketPrecision(maxQuantity.toString(), selectedMarket.id)
    setSellQuantity(formattedQuantity)
    setSellSliderValue(100)

    if (selectedRadioButton === 'limit') {
      // For limit orders, calculate price if limit price is set
      if (sellLimitPrice && parseFloat(sellLimitPrice) > 0) {
        const totalPrice = parseFloat(formattedQuantity) * parseFloat(sellLimitPrice)
        setSellPrice(totalPrice.toString())

        // Calculate fee with the new quantity
        if (parseFloat(formattedQuantity) > 0) {
          debouncedCalculateSellFee(formattedQuantity, null, sellLimitPrice)
        }
      }
    } else {
      // For market orders, calculate estimated price
      const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      if (marketPrice > 0) {
        const estimatedPrice = parseFloat(formattedQuantity) * marketPrice
        setSellPrice(estimatedPrice.toString())
      }

      // Calculate fee with the new quantity
      if (parseFloat(formattedQuantity) > 0) {
        debouncedCalculateSellFee(formattedQuantity, null, null)
      }
    }
  }

  // New fee calculation functions that take direct values
  const calculateBuyFeeWithValue = async (directQuantity, directPrice, directLimitPrice) => {
    console.log('🔥 calculateBuyFeeWithValue called with:', {
      directQuantity,
      directPrice,
      directLimitPrice,
      selectedRadioButton,
      selectedMarketId: selectedMarket?.id
    })

    if (!selectedMarket?.id) {
      console.log('❌ No selected market, aborting fee calculation')
      return
    }

    setBuyFeeLoading(true)
    try {
      // Use provided values if available, otherwise fall back to state
      const currentBuyQuantity = directQuantity !== null ? directQuantity : buyQuantity
      const currentBuyPrice = directPrice !== null ? directPrice : buyPrice
      const currentBuyLimitPrice = directLimitPrice !== null ? directLimitPrice : buyLimitPrice

      console.log('Buy fee calculation debug (with direct values):')
      console.log('- buyQuantity:', currentBuyQuantity, 'type:', typeof currentBuyQuantity)
      console.log('- buyPrice:', currentBuyPrice, 'type:', typeof currentBuyPrice)
      console.log('- buyLimitPrice:', currentBuyLimitPrice, 'type:', typeof currentBuyLimitPrice)
      console.log('- selectedRadioButton:', selectedRadioButton)

      // Always use volume = 1 to get per-unit fee calculation
      let price = 0

      if (selectedRadioButton === 'limit') {
        // For limit orders: use the limit price, or fallback to market price for initial calculation
        price = parseFloat(currentBuyLimitPrice || 0)
        console.log('📊 Limit order - using limit price:', price)

        // If no limit price is set yet, use market price for initial fee calculation
        if (price <= 0) {
          const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
          if (marketPrice > 0) {
            price = marketPrice
            console.log('🔄 No limit price set, using market price for initial fee calculation:', price)
          } else {
            // Try fallback from markets list
            const marketData = marketsList.find(el => el.id === selectedMarket.id)
            const fallbackPrice = parseFloat(marketData?.last || 0)
            if (fallbackPrice > 0) {
              price = fallbackPrice
              console.log('🔄 Using fallback market price for initial fee calculation:', price)
            }
          }
        }
      } else {
        console.log('📊 Market order calculation starting...')
        // For market orders: use current market price
        const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
        console.log('🎯 Market price from ticker:', marketPrice)

        // If market price is not available, try to get it from the markets list
        if (marketPrice <= 0) {
          console.log('⚠️ Market price not available from tickers, checking alternative sources...')
          const marketData = marketsList.find(el => el.id === selectedMarket.id)
          const fallbackPrice = parseFloat(marketData?.last || 0)
          console.log('🔄 Fallback price from market data:', fallbackPrice)

          if (fallbackPrice <= 0) {
            console.log('❌ No market price available, skipping fee calculation until price data loads')
            setBuyFee(null)
            return
          }
          price = fallbackPrice
        } else {
          price = marketPrice
        }
        console.log('✅ Using market price for fee calculation:', price)
      }

      // Validate that we have a positive price
      if (price <= 0) {
        console.log('❌ Invalid price:', price)
        setBuyFee(null)
        return
      }

      console.log('✅ Fee calculation - Price per unit:', price)

      // Determine actual volume in base currency
      let actualVolume = currentBuyQuantity || '1'
      if (!currentBuyQuantity && currentBuyPrice && price > 0) {
        actualVolume = (parseFloat(currentBuyPrice) / parseFloat(price)).toFixed(8)
      }

      const requestBody = {
        market_id: selectedMarket.id,
        ord_type: selectedRadioButton,
        price: price.toString(),
        side: 'buy',
        volume: actualVolume,
        actual_volume: actualVolume
      }

      console.log('🚀 Buy fee request:', requestBody)
      console.log('📊 API Parameters breakdown:')
      console.log('  - price (per unit):', price, '(', selectedRadioButton === 'limit' ? 'user limit price' : 'current market price', ')')
      console.log('  - actual_volume (BTC amount):', actualVolume, '(', selectedRadioButton === 'limit' ? 'user BTC quantity' : 'USD amount ÷ market price', ')')

      const response = await API.post('trading/market/orders/new/estimate_fee', requestBody)
      console.log('✅ Buy fee response:', response.data)
      setBuyFee(response.data)
    } catch (error) {
      console.error('❌ Error fetching buy fee:', error)
    } finally {
      setBuyFeeLoading(false)
    }
  }

  const calculateSellFeeWithValue = async (directQuantity, directPrice, directLimitPrice) => {
    console.log('🔥 calculateSellFeeWithValue called with:', {
      directQuantity,
      directPrice,
      directLimitPrice,
      selectedRadioButton,
      selectedMarketId: selectedMarket?.id
    })

    if (!selectedMarket?.id) {
      console.log('❌ No selected market, aborting fee calculation')
      return
    }

    setSellFeeLoading(true)
    try {
      // Use provided values if available, otherwise fall back to state
      const currentSellQuantity = directQuantity !== null ? directQuantity : sellQuantity
      const currentSellPrice = directPrice !== null ? directPrice : sellPrice
      const currentSellLimitPrice = directLimitPrice !== null ? directLimitPrice : sellLimitPrice

      console.log('Sell fee calculation debug (with direct values):')
      console.log('- sellQuantity:', currentSellQuantity, 'type:', typeof currentSellQuantity)
      console.log('- sellPrice:', currentSellPrice, 'type:', typeof currentSellPrice)
      console.log('- sellLimitPrice:', currentSellLimitPrice, 'type:', typeof currentSellLimitPrice)
      console.log('- selectedRadioButton:', selectedRadioButton)

      // Always use volume = 1 to get per-unit fee calculation
      let price = 0

      if (selectedRadioButton === 'limit') {
        // For limit orders: use the limit price, or fallback to market price for initial calculation
        price = parseFloat(currentSellLimitPrice || 0)
        console.log('📊 Limit order - using limit price:', price)

        // If no limit price is set yet, use market price for initial fee calculation
        if (price <= 0) {
          const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
          if (marketPrice > 0) {
            price = marketPrice
            console.log('🔄 No limit price set, using market price for initial fee calculation:', price)
          } else {
            // Try fallback from markets list
            const marketData = marketsList.find(el => el.id === selectedMarket.id)
            const fallbackPrice = parseFloat(marketData?.last || 0)
            if (fallbackPrice > 0) {
              price = fallbackPrice
              console.log('🔄 Using fallback market price for initial fee calculation:', price)
            }
          }
        }
      } else {
        console.log('📊 Market order calculation starting...')
        // For market orders: use current market price
        const marketPrice = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
        console.log('🎯 Market price from ticker:', marketPrice)

        // If market price is not available, try to get it from the markets list
        if (marketPrice <= 0) {
          console.log('⚠️ Market price not available from tickers, checking alternative sources...')
          const marketData = marketsList.find(el => el.id === selectedMarket.id)
          const fallbackPrice = parseFloat(marketData?.last || 0)
          console.log('🔄 Fallback price from market data:', fallbackPrice)

          if (fallbackPrice <= 0) {
            console.log('❌ No market price available, skipping fee calculation until price data loads')
            setSellFee(null)
            return
          }
          price = fallbackPrice
        } else {
          price = marketPrice
        }
        console.log('✅ Using market price for fee calculation:', price)
      }

      // Validate that we have a positive price
      if (price <= 0) {
        console.log('❌ Invalid price:', price)
        setSellFee(null)
        return
      }

      console.log('✅ Fee calculation - Price per unit:', price)

      const actualVolume = currentSellQuantity || '1'

      const requestBody = {
        market_id: selectedMarket.id,
        ord_type: selectedRadioButton,
        price: price.toString(),
        side: 'sell',
        volume: actualVolume,
        actual_volume: actualVolume
      }

      console.log('🚀 Sell fee request:', requestBody)
      console.log('  - actual_volume (base currency amount):', actualVolume)

      const response = await API.post('trading/market/orders/new/estimate_fee', requestBody)
      console.log('✅ Sell fee response:', response.data)
      setSellFee(response.data)
    } catch (error) {
      console.error('❌ Error fetching sell fee:', error)
    } finally {
      setSellFeeLoading(false)
    }
  }

  // Debounced fee calculation functions
  const debouncedCalculateBuyFee = (directQuantity, directPrice, directLimitPrice) => {
    console.log('🕒 Debouncing buy fee calculation for 1.5 seconds...')

    // Clear existing timer
    if (buyFeeCalculationTimer) {
      clearTimeout(buyFeeCalculationTimer)
      console.log('⏰ Cleared existing buy fee timer')
    }

    // Set loading state immediately
    setBuyFeeLoading(true)

    // Set new timer
    const newTimer = setTimeout(() => {
      console.log('✅ Debounce timer completed, calling buy fee calculation')
      calculateBuyFeeWithValue(directQuantity, directPrice, directLimitPrice)
    }, 1500)

    setBuyFeeCalculationTimer(newTimer)
  }

  const debouncedCalculateSellFee = (directQuantity, directPrice, directLimitPrice) => {
    console.log('🕒 Debouncing sell fee calculation for 1.5 seconds...')

    // Clear existing timer
    if (sellFeeCalculationTimer) {
      clearTimeout(sellFeeCalculationTimer)
      console.log('⏰ Cleared existing sell fee timer')
    }

    // Set loading state immediately
    setSellFeeLoading(true)

    // Set new timer
    const newTimer = setTimeout(() => {
      console.log('✅ Debounce timer completed, calling sell fee calculation')
      calculateSellFeeWithValue(directQuantity, directPrice, directLimitPrice)
    }, 1500)

    setSellFeeCalculationTimer(newTimer)
  }

  // Update original functions to use new ones
  const calculateBuyFee = () => calculateBuyFeeWithValue(null, null, null)
  const calculateSellFee = () => calculateSellFeeWithValue(null, null, null)

  // Calculate initial fees on component mount and when market/order type changes
  useEffect(() => {
    // Clear previous fees
    setBuyFee(null)
    setSellFee(null)

    // Calculate initial fees with volume = 1 to get base fee information
    if (selectedMarket?.id) {
      // Calculate buy fee
      calculateBuyFeeWithValue('1', null, null)

      // Calculate sell fee  
      calculateSellFeeWithValue('1', null, null)
    }

    // Also calculate if we have existing values
    if (buyQuantity && parseFloat(buyQuantity) > 0) {
      debouncedCalculateBuyFee(null, null, null)
    }

    if (sellQuantity && parseFloat(sellQuantity) > 0) {
      debouncedCalculateSellFee(null, null, null)
    }
  }, [selectedRadioButton, selectedMarket?.id])

  // Cleanup timers on unmount or dependency changes
  useEffect(() => {
    return () => {
      if (buyFeeCalculationTimer) {
        clearTimeout(buyFeeCalculationTimer)
      }
      if (sellFeeCalculationTimer) {
        clearTimeout(sellFeeCalculationTimer)
      }
    }
  }, [buyFeeCalculationTimer, sellFeeCalculationTimer])

  // Dynamic price updates when max is active and tickers change
  useEffect(() => {
    if (!marketTickers[selectedMarket?.id]?.last) return

    const marketPrice = parseFloat(marketTickers[selectedMarket.id].last)

    // Update buy quantity when max buy is active and market price changes.
    // buyPrice (the USD cap) is set by handleBuyMax and must not be overwritten here.
    if (isMaxBuyActive && selectedRadioButton === 'market' && buyPrice) {
      const currentBuyPrice = parseFloat(buyPrice)
      if (currentBuyPrice > 0 && marketPrice > 0) {
        // Use best ask from order book to avoid execution price mismatch
        const executionPrice = getBestAskWithBuffer(marketPrice)
        const estimatedQuantity = currentBuyPrice / executionPrice
        const formattedQuantity = formatVolumeToMarketPrecision(estimatedQuantity.toString(), selectedMarket.id)
        setBuyQuantity(formattedQuantity)
      }
    }

    // Update sell prices when max sell is active and market order
    if (isMaxSellActive && selectedRadioButton === 'market' && sellQuantity) {
      if (parseFloat(sellQuantity) > 0 && marketPrice > 0) {
        const estimatedPrice = parseFloat(sellQuantity) * marketPrice
        setSellPrice(estimatedPrice.toString())
      }
    }
  }, [marketTickers, orderBookAsks, selectedMarket?.id, isMaxBuyActive, isMaxSellActive, selectedRadioButton, buyPrice, sellQuantity])

  // Update Total when MAX is active and limit price changes
  useEffect(() => {
    // Update sell total when max sell is active and limit price changes
    if (isMaxSellActive && selectedRadioButton === 'limit' && sellLimitPrice && sellQuantity) {
      const totalPrice = parseFloat(sellQuantity) * parseFloat(sellLimitPrice)
      setSellTotalPrice(totalPrice.toString())
    }
  }, [sellLimitPrice, isMaxSellActive, selectedRadioButton, sellQuantity])

  const handleSubmit = orderType => {
    setSubmitted(true)
    if (
      parseFloat(buyQuantity || buyConvertedPrice ? buyQuantity || buyConvertedPrice : '') <
      parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)
    ) {
      // If there's a form error, prevent the API call and show the error
      console.log('Form submission prevented due to errors.')
      return
    }

    if (
      parseFloat(sellQuantity || sellConvertedPrice ? sellQuantity || sellConvertedPrice : '') <
      parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)
    ) {
      // If there's a form error, prevent the API call and show the error
      console.log('Form submission prevented due to errors2.')
      return
    }

    // Get the raw volume value
    const rawVolume = orderType === 'buy' ? buyQuantity || buyConvertedPrice : sellQuantity || sellConvertedPrice

    // Format the volume according to market precision
    const formattedVolume = formatVolumeToMarketPrecision(rawVolume, selectedMarket.id)

    console.log(`Original volume: ${rawVolume}, Formatted volume: ${formattedVolume}`)

    _addOrder(
      {
        market: selectedMarket.id,
        side: orderType,
        volume: formattedVolume, // Use the formatted volume
        ...(selectedRadioButton === 'limit' && {
          price: orderType === 'buy' ? buyLimitPrice : sellLimitPrice
        }),
        ord_type: selectedRadioButton
      },
      data => {
        dispatch(
          setSnackbarData({
            alertMessage: 'Order set',
            buttonMessage: 'See order',
            callback: () => { },
            actionType: 'link', // link - close
            linkTo: '/account/orders/open',
            severity: 'success',
            position: 'topRight'
          })
        )
        dispatch(setSnackbarOpen(true))
        dispatch(startPolling()) // Start polling for order updates
        resetInputValues()
        setRefreshBalance(!refreshBalance)
      },
      error => {
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
            severity: 'error',
            callback: () => { },
            position: 'topRight'
          })
        )
      }
    )
  }

  // const handleFormValidation = () => {
  //   setSubmitted(true)

  //   const minAmount = parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)

  //   // Check for buy quantity or price and validate minimum amount
  //   if (
  //     (parseFloat(buyQuantity || buyConvertedPrice) < minAmount) ||
  //     (parseFloat(sellQuantity || sellConvertedPrice) < minAmount)
  //   ) {
  //     setFormError(true)  // Set the error flag to true if any error exists
  //     return true
  //   }

  //   setFormError(false)  // No error
  //   return false
  // }

  const handleBuyTotalPriceChange = event => {
    console.log(event.target.value)
    setBuyTotalPrice(event.target.value)
    setBuyQuantity('')
    setIsMaxBuyActive(false) // Reset MAX state when manually changing total
  }

  const handleSellTotalPriceChange = event => {
    setSellTotalPrice(event.target.value)
    setSellQuantity('')
    setIsMaxSellActive(false) // Reset MAX state when manually changing total
  }

  const coinNamePrice = useSelector(state => state.selectedMarket.value?.name?.split('/')[1])

  // Function to get the market's precision as a readable message
  const getPrecisionMessage = marketId => {
    if (!marketId) return ''

    const market = marketsList.find(el => el.id === marketId)
    if (!market) return ''

    const precision = market.amount_precision || market.step_size || 8
    return `Max ${precision} decimal places`
  }

  // Helper function to format number with proper precision based on currency type
  const formatWithCurrencyPrecision = (number, currencyType = 'base') => {
    if (!number) return '0'
    const num = parseFloat(number)

    // Get precision from market data
    let precision = 8 // default fallback
    if (selectedMarket) {
      if (currencyType === 'base') {
        // For base currency (e.g., BTC in BTC/USDT), use amount_precision or step_size
        precision = selectedMarket.amount_precision || selectedMarket.step_size || 8
      } else if (currencyType === 'quote') {
        // For quote currency (e.g., USDT in BTC/USDT), use price_precision
        precision = selectedMarket.price_precision || 2
      }
    }

    // Format to the determined precision then remove trailing zeros
    return num.toFixed(precision).replace(/\.?0+$/, '');
  }

  // Helper function to calculate actual fee based on user input amount
  const calculateActualBuyFee = () => {
    if (!buyFee) return '0.00'

    // Check if user has actually entered values or MAX is active
    let hasUserInput = false
    let userAmount = 0

    if (selectedRadioButton === 'limit') {
      // For limit orders: check if both quantity and limit price are entered
      const quantity = parseFloat(buyQuantity || 0)
      const limitPrice = parseFloat(buyLimitPrice || 0)
      hasUserInput = (quantity > 0 && limitPrice > 0) || isMaxBuyActive
      userAmount = quantity * limitPrice
    } else {
      // For market orders: check if price is entered
      userAmount = parseFloat(buyPrice || 0)
      hasUserInput = userAmount > 0 || isMaxBuyActive
    }

    // Only show fees if user has entered values or MAX is active
    if (!hasUserInput) return '0.00'

    // Primary: Use actual_trade.fee.amount from API response if available
    if (buyFee.actual_trade && buyFee.actual_trade.fee && buyFee.actual_trade.fee.amount) {
      const feeAmount = parseFloat(buyFee.actual_trade.fee.amount)
      return feeAmount.toFixed(8).replace(/\.?0+$/, ''); // Remove trailing zeros
    }

    // Fallback: Calculate based on user input and fee percentage
    if (!buyFee.fee_percentage) return '0.00'

    const feeAmount = (userAmount * parseFloat(buyFee.fee_percentage)) / 100
    return feeAmount.toFixed(8).replace(/\.?0+$/, ''); // Remove trailing zeros
  }

  const calculateActualSellFee = () => {
    if (!sellFee) return '0.00'

    // Check if user has actually entered values or MAX is active
    let hasUserInput = false
    let userAmount = 0

    if (selectedRadioButton === 'limit') {
      // For limit orders: check if both quantity and limit price are entered
      const quantity = parseFloat(sellQuantity || 0)
      const limitPrice = parseFloat(sellLimitPrice || 0)
      hasUserInput = (quantity > 0 && limitPrice > 0) || isMaxSellActive
      userAmount = quantity * limitPrice
    } else {
      // For market orders: check if price is entered
      userAmount = parseFloat(sellPrice || 0)
      hasUserInput = userAmount > 0 || isMaxSellActive
    }

    // Only show fees if user has entered values or MAX is active
    if (!hasUserInput) return '0.00'

    // Primary: Use actual_trade.fee.amount from API response if available
    if (sellFee.actual_trade && sellFee.actual_trade.fee && sellFee.actual_trade.fee.amount) {
      const feeAmount = parseFloat(sellFee.actual_trade.fee.amount)
      return feeAmount.toFixed(8).replace(/\.?0+$/, ''); // Remove trailing zeros
    }

    // Fallback: Calculate based on user input and fee percentage
    if (!sellFee.fee_percentage) return '0.00'

    const feeAmount = (userAmount * parseFloat(sellFee.fee_percentage)) / 100
    return feeAmount.toFixed(8).replace(/\.?0+$/, ''); // Remove trailing zeros
  }

  // Helper function to get fee currency from API response
  const getBuyFeeCurrency = () => {
    if (buyFee && buyFee.actual_trade && buyFee.actual_trade.fee && buyFee.actual_trade.fee.currency) {
      return buyFee.actual_trade.fee.currency.toUpperCase()
    }
    return selectedMarket?.quote_unit?.toUpperCase() || 'USD'
  }

  const getSellFeeCurrency = () => {
    if (sellFee && sellFee.actual_trade && sellFee.actual_trade.fee && sellFee.actual_trade.fee.currency) {
      return sellFee.actual_trade.fee.currency.toUpperCase()
    }
    return selectedMarket?.quote_unit?.toUpperCase() || 'USD'
  }

  return (
    <div className={Styles.card}>
      {/* radio buttons container */}
      <div className={Styles.radioButtonsContainer}>
        <RadioButton
          text={<FormattedMessage id='page.markets.trade.limit' />}
          icon={<FlagIcon />}
          isSelected={selectedRadioButton === 'limit'}
          onClick={() => {
            if (selectedRadioButton === 'limit') return
            setSelectedRadioButton('limit')
          }}
        />
        <RadioButton
          text={<FormattedMessage id='page.markets.trade.market' />}
          icon={<MarketIcon width={12} />}
          isSelected={selectedRadioButton === 'market'}
          onClick={() => {
            if (selectedRadioButton === 'market') return
            setSelectedRadioButton('market')
          }}
        />

        <ToolTip
          toolTipStyle={{
            margin: '0 0 auto auto'
          }}
          text={
            <>
              <div>
                Limit: Want to buy or sell at a specific price? A limit order lets you do that, but remember that it
                might not always go through.A limit order lets you do that, but remember that it might not always go
                through.
              </div>
              <div
                style={{
                  marginTop: 20
                }}
              >
                Market: Buying or selling now means you'll get a quick trade, taking crypto prices from the current
                market.
              </div>
            </>
          }
        />
      </div>

      {/* MAIN Buy/Sell container */}
      <div className={Styles.mainBuySellContainer}>
        {/* Buy Container */}
        <div className={Styles.buyContainer}>
          <div style={{ flex: 1 }}>
            <span className={Styles.balanceAvailable}>
              {userData.email &&
                (balances ? (
                  balances.find(el => el.currency === toCurrency)?.balance ? (
                    `Available balance: ${formatWithCurrencyPrecision(balances.find(el => el.currency === toCurrency)?.balance || '0', 'quote')} ${toCurrency?.toUpperCase()}`
                  ) : (
                    `Available balance: ${formatWithCurrencyPrecision('0', 'quote')} ${toCurrency?.toUpperCase()}`
                  )
                ) : (
                  <ShimmerLine height='10px' width='70px' />
                ))}
            </span>
            <ModernCurrencyInput
              title={selectedRadioButton === 'limit' ? `Limit Price` : 'Estimated Price'}
              onChange={selectedRadioButton === 'limit' ? handleBuyLimitPriceChange : handleBuyPriceChange}
              type='number'
              onDropdownClick={() => { }}
              disabled={isMaxBuyActive && selectedRadioButton === 'market'}
              placeholder={selectedRadioButton === 'limit' ? '0.00' : buyConvertedPrice ? buyConvertedPrice : '0.00'}
              value={selectedRadioButton === 'limit' ? buyLimitPrice : buyPrice}
              containerStyle={{
                marginTop: 15
              }}
              currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.quote_url}
              coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[1])}
              hint={selectedRadioButton === 'market' ? 'Based on current market price' : ''}
              icon={
                userData.email && selectedRadioButton === 'market' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                      text={isMaxBuyActive ? "REVERT" : "MAX"}
                      variant="link"
                      onClick={handleBuyMax}
                      style={{
                        padding: 0,
                        width: 'fit-content',
                        fontSize: '12px',
                        color: isMaxBuyActive ? 'var(--primary-color)' : 'var(--gray-color)',
                        fontWeight: isMaxBuyActive ? 'bold' : 'normal'
                      }}
                    />
                  </div>
                )
                // Commented out for limit orders
                // userData.email && selectedRadioButton === 'limit' && (
                //   <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                //     <Button
                //       text={isMaxBuyActive ? "REVERT" : "MAX"}
                //       variant="link"
                //       onClick={handleBuyMax}
                //       style={{
                //         padding: 0,
                //         width: 'fit-content',
                //         fontSize: '12px',
                //         color: isMaxBuyActive ? 'var(--primary-color)' : 'var(--gray-color)',
                //         fontWeight: isMaxBuyActive ? 'bold' : 'normal'
                //       }}
                //     />
                //   </div>
                // )
              }
            />
            <ModernCurrencyInput
              title={`Amount`}
              onChange={handleBuyQuantityChange}
              type='number'
              onDropdownClick={() => { }}
              placeholder='0.00'
              value={buyQuantity || buyConvertedPrice ? buyQuantity || buyConvertedPrice : ''}
              disabled={isMaxBuyActive && selectedRadioButton === 'market'}
              containerStyle={{
                marginTop: 15
              }}
              error={
                (submitted &&
                  parseFloat(buyQuantity || buyConvertedPrice ? buyQuantity || buyConvertedPrice : '') <
                  parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount))
                  ? {
                    message: `Minimum deposit amount is ${marketsList.find(el => el.id === selectedMarket.id)?.min_amount
                      }`
                  }
                  : (isBuyAmountExceedingBalance && (buyQuantity || buyPrice))
                    ? {
                      message: `Amount exceeds available balance: ${balances?.find(el => el.currency === toCurrency)?.balance || 0} ${toCurrency?.toUpperCase()}`
                    }
                    : isBuyAmountExceedsAllowedLimit
                      ? {
                        message: `Amount exceeds allowed limit of ${formatWithCurrencyPrecision(
                          String(getBuyAllowedToTrade() ?? 0),
                          'quote'
                        )} ${toCurrency?.toUpperCase()}`
                      }
                      : undefined
              }
              coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[0])}
              currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.icon_url}
              hint={selectedRadioButton === 'market' ? getPrecisionMessage(selectedMarket?.id) : ''}
            />
            {/* slider here */}
            {userData.email && selectedRadioButton === 'market' && (
              <Slider
                min={0}
                max={100}
                marks={sliderMarks}
                value={buySliderValue}
                onChange={handleBuySliderChange}
                trackStyle={[{ backgroundColor: 'var(--primary-color)' }]}
                railStyle={{ backgroundColor: 'var(--gray-color)' }}
                handleStyle={{
                  height: 12,
                  width: 12,
                  marginTop: -3.5,
                  backgroundColor: 'white',
                  border: `2px solid var(--primary-color)`,
                  borderRadius: '50%',
                  boxShadow: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                dotStyle={{
                  backgroundColor: 'var(--gray-color)',
                  borderColor: 'var(--gray-color)'
                }}
                activeDotStyle={{
                  backgroundColor: 'var(--primary-color)',
                  borderColor: 'var(--primary-color)'
                }}
                style={{ margin: '30px auto', width: '90%' }}
              />
            )}
            {selectedRadioButton === 'limit' && (
              <ModernCurrencyInput
                title={`Total`}
                // currencyIcon=""
                onChange={handleBuyTotalPriceChange}
                type='number'
                // disabled={true}
                onDropdownClick={() => { }}
                placeholder='0.00'
                value={
                  buyTotalPrice
                    ? buyTotalPrice
                    : buyPrice || buyLimitPrice * buyQuantity
                      ? buyPrice || buyLimitPrice * buyQuantity
                      : ''
                }
                containerStyle={{
                  marginTop: 15
                }}
                coinName={coinNamePrice}
                icon={
                  userData.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Button
                        text={isMaxBuyActive ? "REVERT" : "MAX"}
                        variant="link"
                        onClick={() => {
                          if (isMaxBuyActive) {
                            // Revert: Reset to manual input mode
                            setIsMaxBuyActive(false)
                            setBuyTotalPrice('')
                            setBuyQuantity('')
                            setBuyLimitPrice('')
                            setBuySliderValue(0)
                            setBuyFee(null)
                          } else {
                            // Set MAX: Fill with available balance after fees
                            setIsMaxBuyActive(true)
                            setIsMaxSellActive(false)

                            // Primary: Use allowed_to_trade from API response (quote currency for buy orders)
                            let availableBalance = 0
                            if (buyFee && buyFee.allowed_to_trade) {
                              if (typeof buyFee.allowed_to_trade === 'object') {
                                // New structure: get quote currency amount from allowed_to_trade object
                                const quoteCurrency = selectedMarket?.quote_unit || toCurrency
                                availableBalance = parseFloat(buyFee.allowed_to_trade[quoteCurrency] || 0)
                              } else {
                                // Legacy structure: use fee_per_unit calculation
                                if (buyFee.fee_per_unit && parseFloat(buyFee.fee_per_unit) >= 0) {
                                  const balance = parseFloat(balances?.find(el => el.currency === toCurrency)?.balance || 0)
                                  const feePerUnit = parseFloat(buyFee.fee_per_unit)
                                  availableBalance = balance - feePerUnit
                                }
                              }
                            }

                            // Fallback: Calculate balance - fees if no API data available
                            if (availableBalance <= 0) {
                              const rawBalance = parseFloat(balances?.find(el => el.currency === toCurrency)?.balance || 0)
                              const feePercentage = buyFee ? parseFloat(buyFee.fee_percentage || 0) : 0
                              const feeAmount = rawBalance * (feePercentage / 100)
                              availableBalance = rawBalance - feeAmount
                            }

                            setBuyTotalPrice(availableBalance.toString())
                            setBuySliderValue(100)

                            // Calculate fee with the new total price
                            if (availableBalance > 0) {
                              debouncedCalculateBuyFee(null, availableBalance.toString(), null)
                            }
                          }
                        }}
                        style={{
                          padding: 0,
                          width: 'fit-content',
                          fontSize: '12px',
                          color: isMaxBuyActive ? 'var(--primary-color)' : 'var(--gray-color)',
                          fontWeight: isMaxBuyActive ? 'bold' : 'normal'
                        }}
                      />
                    </div>
                  )
                }
              />
            )}
            {loggedIn && (
              <div className={Styles.feesContainer}>
                <div className={Styles.feesInfoContainer}>
                  <span className={Styles.feesInfoRow}>
                    <InfoIcon color={'var(--gray-color)'} width={12} />
                    Fees to trade:{' '}
                  </span>
                  <span style={{ color: 'var(--gray-color)' }}>
                    {buyFeeLoading ? (
                      <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Calculating...</span>
                    ) : buyFee ? (
                      `${buyFee.fee?.amount ?? calculateActualBuyFee()} ${getBuyFeeCurrency()} (${formatNumber(buyFee.fee_percentage)}%)`
                    ) : (
                      `0.00 ${getBuyFeeCurrency()} (0%)`
                    )}
                  </span>
                </div>
                <div className={Styles.feesInfoContainer}>
                  <span className={Styles.feesInfoRow}>
                    <InfoIcon color={'var(--gray-color)'} width={12} />
                    Allowed to buy with:{' '}
                  </span>
                  <span style={{ color: 'var(--gray-color)', marginTop: '5px' }}>
                    {buyFeeLoading ? (
                      <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Calculating...</span>
                    ) : (
                      (() => {
                        // Show allowed_to_trade from estimate_fee (already net of fees),
                        // not the raw wallet balance.
                        const allowedAmount = getBuyAllowedToTrade()
                        if (allowedAmount === null) return `≈ 0 ${toCurrency?.toUpperCase()}`
                        return (
                          <span
                            title={`${allowedAmount} ${toCurrency?.toUpperCase()} allowed to trade (from estimate_fee)`}
                            style={{ cursor: 'help' }}
                          >
                            {`≈ ${formatWithCurrencyPrecision(allowedAmount.toString(), 'quote')} ${toCurrency?.toUpperCase()}`}
                          </span>
                        )
                      })()
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sell Container */}
        <div className={Styles.sellContainer}>
          <div style={{ flex: 1 }}>
            <span className={Styles.balanceAvailable}>
              {userData.email &&
                (balances ? (
                  balances.find(el => el.currency === fromCurrency)?.balance ? (
                    `Available balance: ${formatWithCurrencyPrecision(balances.find(el => el.currency === fromCurrency)?.balance || '0', 'base')} ${fromCurrency?.toUpperCase()}`
                  ) : (
                    `Available balance: ${formatWithCurrencyPrecision('0', 'base')} ${fromCurrency?.toUpperCase()}`
                  )
                ) : (
                  <ShimmerLine height='10px' width='70px' />
                ))}
            </span>
            <ModernCurrencyInput
              title={selectedRadioButton === 'limit' ? `Limit Price` : 'Estimated Price'}
              onChange={selectedRadioButton === 'limit' ? handleSellLimitPriceChange : handleSellPriceChange}
              disabled={isMaxSellActive && selectedRadioButton === 'market'}
              type='number'
              onDropdownClick={() => { }}
              placeholder={selectedRadioButton === 'limit' ? '0.00' : sellConvertedPrice ? sellConvertedPrice : '0.00'}
              value={selectedRadioButton === 'limit' ? sellLimitPrice : sellPrice}
              containerStyle={{
                marginTop: 15
              }}
              currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.quote_url}
              coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[1])}
              hint={selectedRadioButton === 'market' ? 'Based on current market price' : ''}
            />
            <ModernCurrencyInput
              title={`Amount`}
              onChange={handleSellQuantityChange}
              type='number'
              onDropdownClick={() => { }}
              placeholder='0.00'
              value={sellQuantity || sellConvertedPrice ? sellQuantity || sellConvertedPrice : ''}
              disabled={isMaxSellActive}
              containerStyle={{
                marginTop: 15
              }}
              error={
                (submitted &&
                  parseFloat(sellQuantity || sellConvertedPrice ? sellQuantity || sellConvertedPrice : '') <
                  parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount))
                  ? {
                    message: `Minimum deposit amount is ${marketsList.find(el => el.id === selectedMarket.id)?.min_amount
                      }`
                  }
                  : (isSellAmountExceedingBalance && sellQuantity)
                    ? {
                      message: `Amount exceeds available balance: ${balances?.find(el => el.currency === fromCurrency)?.balance || 0} ${fromCurrency?.toUpperCase()}`
                    }
                    : isSellAmountExceedsAllowedLimit
                      ? {
                        message: `Amount exceeds allowed limit of ${formatWithCurrencyPrecision(
                          typeof sellFee?.allowed_to_trade === 'object'
                            ? String(sellFee?.allowed_to_trade?.[fromCurrency] ?? sellFee?.allowed_to_trade?.[selectedMarket?.base_unit] ?? 0)
                            : String(sellFee?.allowed_to_trade ?? 0),
                          'base'
                        )} ${selectedMarket?.base_unit?.toUpperCase()}`
                      }
                      : undefined
              }
              coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[0])}
              currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.icon_url}
              hint={selectedRadioButton === 'market' ? getPrecisionMessage(selectedMarket?.id) : ''}
              icon={
                userData.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                      text={isMaxSellActive ? "REVERT" : "MAX"}
                      variant="link"
                      onClick={handleSellMax}
                      style={{
                        padding: 0,
                        width: 'fit-content',
                        fontSize: '12px',
                        color: isMaxSellActive ? 'var(--primary-color)' : 'var(--gray-color)',
                        fontWeight: isMaxSellActive ? 'bold' : 'normal'
                      }}
                    />
                  </div>
                )
              }
            />

            {/* slider here */}
            {userData.email && selectedRadioButton === 'market' && (
              <Slider
                min={0}
                max={100}
                marks={sliderMarks}
                value={sellSliderValue}
                onChange={handleSellSliderChange}
                trackStyle={[{ backgroundColor: 'var(--primary-color)' }]}
                railStyle={{ backgroundColor: 'var(--gray-color)' }}
                handleStyle={{
                  height: 12,
                  width: 12,
                  marginTop: -3.5,
                  backgroundColor: 'white',
                  border: `2px solid var(--primary-color)`,
                  borderRadius: '50%',
                  boxShadow: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                dotStyle={{
                  backgroundColor: 'var(--gray-color)',
                  borderColor: 'var(--gray-color)'
                }}
                activeDotStyle={{
                  backgroundColor: 'var(--primary-color)',
                  borderColor: 'var(--primary-color)'
                }}
                style={{ margin: '30px auto', width: '90%' }}
              />
            )}
            {selectedRadioButton === 'limit' && (
              <ModernCurrencyInput
                title={`Total`}
                currencyIcon=''
                onChange={handleSellTotalPriceChange}
                type='number'
                // disabled={true}
                onDropdownClick={() => { }}
                placeholder='0.00'
                value={
                  sellTotalPrice
                    ? sellTotalPrice
                    : sellPrice || sellLimitPrice * sellQuantity
                      ? sellPrice || sellLimitPrice * sellQuantity
                      : ''
                }
                containerStyle={{
                  marginTop: 15
                }}
                coinName={coinNamePrice}
              />
            )}
            {loggedIn && (
              <div className={Styles.feesContainer}>
                <div className={Styles.feesInfoContainer}>
                  <span className={Styles.feesInfoRow}>
                    <InfoIcon color={'var(--gray-color)'} width={12} />
                    Fees to trade:{' '}
                  </span>
                  <span style={{ color: 'var(--gray-color)' }}>
                    {sellFeeLoading ? (
                      <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Calculating...</span>
                    ) : sellFee ? (
                      `${sellFee.fee?.amount ?? calculateActualSellFee()} ${getSellFeeCurrency()} (${formatNumber(sellFee?.fee_percentage)}%)`
                    ) : (
                      `0.00 ${getSellFeeCurrency()} (0%)`
                    )}
                  </span>
                </div>
                <div className={Styles.feesInfoContainer}>
                  <span className={Styles.feesInfoRow}>
                    <InfoIcon color={'var(--gray-color)'} width={12} />
                    Allowed to sell:{' '}
                  </span>
                  <span style={{ color: 'var(--gray-color)', marginTop: '5px' }}>
                    {sellFeeLoading ? (
                      <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Calculating...</span>
                    ) : (
                      (() => {
                        // Show allowed_to_trade from estimate_fee (base currency),
                        // not the raw wallet balance.
                        const allowedAmount = getSellAllowedToTrade()
                        if (allowedAmount === null) return `≈ 0 ${fromCurrency?.toUpperCase()}`
                        return (
                          <span
                            title={`${allowedAmount} ${fromCurrency?.toUpperCase()} allowed to trade (from estimate_fee)`}
                            style={{ cursor: 'help' }}
                          >
                            {`≈ ${formatWithCurrencyPrecision(allowedAmount.toString(), 'base')} ${fromCurrency?.toUpperCase()}`}
                          </span>
                        )
                      })()
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Disclaimer - spans across both components, above buttons */}
      {loggedIn && (
        <div style={{
          margin: '20px 0 15px 0',
          padding: '12px',
          backgroundColor: 'var(--background-secondary)',
          borderRadius: '6px',
          fontSize: '14px',
          color: 'var(--gray-color)',
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          Once the authorised allowed limit has been completely utilised, no remaining balance will be available, resulting in a balance of zero (0).
        </div>
      )}

      {/* Buttons Row */}
      <div style={{
        display: 'flex',
        gap: '30px',
        marginTop: loggedIn ? '0px' : '20px'
      }}>
        <div style={{ flex: 1 }}>
          {userData.email ? (
            <Button
              text={`Buy ${marketsList.find(el => el.id === selectedMarket.id)?.base_unit.toUpperCase()}`}
              style={{
                marginTop: 0,
                backgroundColor: isBuyButtonDisabled ? '#6b7280' : '#10B981',
                color: 'white',
                opacity: isBuyButtonDisabled ? 0.6 : 1,
                cursor: isBuyButtonDisabled ? 'not-allowed' : 'pointer',
                width: '100%'
              }}
              onClick={e => {
                if (!isBuyButtonDisabled) {
                  handleSubmit('buy')
                }
              }}
              disabled={isBuyButtonDisabled}
              className={Styles.buyButton}
            />
          ) : (
            <Button type='loggedOut' />
          )}
        </div>
        <div style={{ flex: 1 }}>
          {userData.email ? (
            <Button
              text={`Sell ${marketsList.find(el => el.id === selectedMarket.id)?.base_unit.toUpperCase()}`}
              style={{
                marginTop: 0,
                backgroundColor: isSellButtonDisabled ? '#6b7280' : 'rgba(240,68,68,255)',
                color: 'white',
                opacity: isSellButtonDisabled ? 0.6 : 1,
                cursor: isSellButtonDisabled ? 'not-allowed' : 'pointer',
                width: '100%'
              }}
              onClick={e => {
                if (!isSellButtonDisabled) {
                  handleSubmit('sell')
                }
              }}
              disabled={isSellButtonDisabled}
            />
          ) : (
            <Button type='loggedOut' />
          )}
        </div>
      </div>
    </div>
  )
}

export default injectIntl(MarketForm)